import {
  analyse,
  comparablePrice,
  fmtNum,
  fmtSize,
  lei,
  timeAgo,
  type ActivityRow,
  type LedgerShare,
  type Rfq,
  type SiteDocument,
  t,
  intlLocale,
} from '@procumi/shared';
import { useState, type ReactNode } from 'react';
import { Avatar } from './Avatar';
import { useCountUp } from './ControlPanel';

/**
 * Fila șantierului, după macheta proprietarului (2026-10-13): antet cu desenul
 * clădirii și pașii cererii în lucru, cifre cu iconițe colorate, comparația
 * ofertelor pe poziții, inelul cheltuielilor, furnizorii cererii, activitatea
 * și documentele primite. „Combinația” trimite la cerere, unde comanda se
 * împarte pe furnizori (cerut de proprietar, 2026-10-16).
 */

const ZI = new Intl.DateTimeFormat(intlLocale(), { day: '2-digit', month: 'short' });
const day = (iso: string) => ZI.format(new Date(iso)).replace('.', '');

const ico = (d: string, size = 18) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
export const ICONS = {
  pin: 'M8 14.5s4.8-4.3 4.8-8.1A4.8 4.8 0 0 0 3.2 6.4c0 3.8 4.8 8.1 4.8 8.1zM8 8.2a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
  truck:
    'M1.5 4h8v7h-8zM9.5 6.5h3l2 2.5v2h-5M4.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6zM11.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6z',
  user: 'M8 7.5a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4zM2.8 14a5.2 5.2 0 0 1 10.4 0',
  wallet: 'M2.5 4.5h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-8a1.5 1.5 0 0 1 1.5-1.5h8v2M10.5 9h3',
  box: 'M8 1.8 13.5 4.6v6.8L8 14.2 2.5 11.4V4.6zM2.5 4.6 8 7.4l5.5-2.8M8 7.4v6.8',
  piggy:
    'M3 8.5a4.5 3.8 0 0 1 9 0c0 1.6-1 2.9-2.4 3.5v1.5H8V12.3a6 6 0 0 1-1 0v1.2H5.4v-1.5A3.8 3.8 0 0 1 3 8.5zM12 7.5l1.6-.8v2.6L12 9.5M6.2 5.2l1-2 1.2 1.4M5.5 8h.01',
  clock: 'M8 14.2A6.2 6.2 0 1 0 8 1.8a6.2 6.2 0 0 0 0 12.4zM8 4.6V8l2.4 1.6',
  check: 'm3.5 8.5 3 3 6-7',
  doc: 'M9.2 1.8H4.8A1.6 1.6 0 0 0 3.2 3.4v9.2a1.6 1.6 0 0 0 1.6 1.6h6.4a1.6 1.6 0 0 0 1.6-1.6V5.4L9.2 1.8ZM9 2v3.2h3.6',
  down: 'M8 2.5v8M4.5 7.5 8 11l3.5-3.5M3 13.5h10',
  table: 'M2 3h12v10H2zM2 6.5h12M6.5 6.5V13',
  pie: 'M8 2a6 6 0 1 0 6 6H8z M9.5 1.6A6 6 0 0 1 14.4 6.5H9.5z',
  people:
    'M6 7a2.3 2.3 0 1 0 0-4.6A2.3 2.3 0 0 0 6 7zM1.8 13.5a4.2 4.2 0 0 1 8.4 0M11 7.2a2 2 0 1 0-.6-3.9M12.2 13.5h2a3.7 3.7 0 0 0-2.6-3.6',
  pulse: 'M1.5 8h3l1.5-4 3 8 1.5-4h4',
  folder:
    'M1.8 4.2A1.2 1.2 0 0 1 3 3h3.2l1.4 1.6H13a1.2 1.2 0 0 1 1.2 1.2v6.4A1.2 1.2 0 0 1 13 13.4H3a1.2 1.2 0 0 1-1.2-1.2z',
  arrow: 'M3 8h10M9 4l4 4-4 4',
};
export const Ico = ({ name, size }: { name: keyof typeof ICONS; size?: number }) => ico(ICONS[name], size);

/** Desenul clădirii, în locul pozei: aceeași planșă ca în bara laterală. */
export function SiteArt({ closed }: { closed: boolean }) {
  return (
    <span className={`sb-art${closed ? ' off' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 88 88" width="88" height="88">
        <defs>
          <linearGradient id="sb-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2B3BFF" />
            <stop offset="1" stopColor="#2B3BFF" />
          </linearGradient>
        </defs>
        <rect width="88" height="88" rx="14" fill="url(#sb-sky)" />
        <g stroke="rgba(255,255,255,.18)" strokeWidth="1">
          <path d="M0 22h88M0 44h88M0 66h88M22 0v88M44 0v88M66 0v88" />
        </g>
        <g fill="none" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round">
          <path d="M14 74V40h22v34M36 74V26h26v48M62 74V48h14v26" />
          <path d="M8 74h74" />
          <path d="M66 26V12M58 14h22M66 14l10 10" opacity=".7" />
        </g>
        <g fill="#fff" opacity=".85">
          {[0, 1, 2, 3].map((r) =>
            [0, 1, 2].map((c) => (
              <rect key={`${r}${c}`} x={41 + c * 7} y={32 + r * 9} width="4" height="5" rx="1" />
            )),
          )}
          {[0, 1, 2].map((r) => (
            <rect key={`l${r}`} x="19" y={46 + r * 9} width="12" height="4" rx="1" />
          ))}
        </g>
      </svg>
    </span>
  );
}

export interface Step {
  label: string;
  sub: string;
  state: 'done' | 'now' | 'next';
}

/** Pașii cererii în lucru: trimisă → oferte → comparare → decizie. */
export function rfqSteps(r: Rfq): Step[] {
  const sent = r.sends
    .map((s) => s.sentAt)
    .filter((x): x is string => !!x)
    .sort();
  const got = r.sends.filter((s) => s.offer);
  const last = got
    .map((s) => s.offer!.receivedAt)
    .sort()
    .pop();
  const a = analyse(r);
  const done = [
    sent.length > 0 || got.length > 0,
    got.length > 0,
    a.full.length >= 2 || !!r.chosen,
    !!r.chosen,
  ];
  const subs = [
    sent[0] ? day(sent[0]) : day(r.createdAt),
    t('{0} din {1}{2}', got.length, r.sends.length, last ? ` · ${day(last)}` : ''),
    a.full.length >= 2 ? t('{0} complete', a.full.length) : t('după 2 oferte'),
    r.chosen
      ? r.chosen.split?.length
        ? `${r.chosen.supplier.name} +${r.chosen.split.length - 1}`
        : r.chosen.supplier.name
      : t('în curs'),
  ];
  const labels = [t('Cerere trimisă'), t('Oferte primite'), t('Comparare'), t('Decizie')];
  const now = done.indexOf(false);
  return labels.map((label, i) => ({
    label,
    sub: subs[i]!,
    state: done[i] ? 'done' : i === now ? 'now' : 'next',
  }));
}

export function Stepper({ steps }: { steps: Step[] }) {
  return (
    <ol className="sb-steps">
      {steps.map((s, i) => (
        <li key={s.label} className={s.state} style={{ ['--i' as string]: i }}>
          <span className="sb-dot">{s.state === 'done' ? <Ico name="check" size={12} /> : null}</span>
          <b>{s.label}</b>
          <span>{s.sub}</span>
        </li>
      ))}
    </ol>
  );
}

export interface Tile {
  key: string;
  icon: keyof typeof ICONS;
  tone: 'blue' | 'night' | 'green' | 'orange';
  label: string;
  value: number | null;
  /** când `value` lipsește */
  empty?: string;
  format?: (n: number) => string;
  sub: ReactNode;
  meter?: number;
}

function TileValue({ value, format }: { value: number; format: (n: number) => string }) {
  const v = useCountUp(value);
  return <>{format(Math.round(v))}</>;
}

export function Tiles({ items }: { items: Tile[] }) {
  return (
    <div className="sb-tiles">
      {items.map((t, i) => (
        <div key={t.key} className={`sheet sb-tile pc-in ${t.tone}`} style={{ ['--i' as string]: i }}>
          <span className="sb-ico">
            <Ico name={t.icon} size={20} />
          </span>
          <span className="sb-tile-b">
            <span className="sb-tile-l">{t.label}</span>
            <b className="sb-tile-v">
              {t.value == null ? t.empty : <TileValue value={t.value} format={t.format ?? fmtNum} />}
            </b>
            <span className="sb-tile-s">{t.sub}</span>
            {t.meter != null ? (
              <span className="sb-meter" aria-hidden="true">
                <span style={{ ['--w' as string]: `${Math.round(Math.min(1, t.meter) * 100)}%` }} />
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Ofertele cererii pe poziții, ca în macheta: prețul unitar pe fiecare furnizor,
 * cel mai mic pe rând în verde, totalul jos, cea mai bună ofertă întreagă marcată.
 */
export function OfferCompare({ rfq, onOpen }: { rfq: Rfq; onOpen: () => void }) {
  const a = analyse(rfq);
  const cols = a.cols.slice(0, 4);
  if (!cols.length) {
    return (
      <div className="empty" style={{ padding: '16px 20px' }}>
        {t('Încă nicio ofertă. Tabelul se umple pe măsură ce răspund furnizorii.')}
      </div>
    );
  }
  const best =
    a.bestTotal != null
      ? a.full.find((c) => c.total === a.bestTotal)
      : a.full.length === 1 && !a.full[0]!.costUnknown
        ? a.full[0]
        : null;
  // Împărțită deja: totalul ales de om, nu combinația ideală.
  const split = rfq.chosen?.split?.length ? (rfq.chosen.splitTotal ?? null) : null;
  // o împărțire care ia de la cineva cu transportul necunoscut nu promite nicio economie
  const mix = split ?? (a.mixUnknown ? null : a.mix);
  const gain = mix != null && a.bestTotal != null ? a.bestTotal - mix : 0;
  return (
    <>
      <div className="sb-cmp-wrap">
        <table className="sb-cmp">
          <thead>
            <tr>
              <th>{t('Material / poziție')}</th>
              {cols.map((c) => (
                <th key={c.send.id} className={c === best ? 'win' : undefined}>
                  <span>{c.supplier.name}</span>
                  {c === best ? (
                    <em>{t('Cea mai bună ofertă')}</em>
                  ) : !c.complete ? (
                    <em className="part">{t('incompletă')}</em>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rfq.items.map((it) => {
              const low = a.bestRow[it.id];
              return (
                <tr key={it.id}>
                  <td>
                    <b>{it.desc}</b>
                    <span>{t('{0} {1} · lei/{2}', fmtNum(it.qty), it.unit, it.unit)}</span>
                  </td>
                  {cols.map((c) => {
                    const p = comparablePrice(c.send.offer!, it);
                    const top = p != null && low != null && Math.abs(p - low) < 0.005;
                    return (
                      <td key={c.send.id} className={top ? 'low' : undefined}>
                        {p == null ? <span className="muted">—</span> : fmtNum(p)}
                        {top ? (
                          <span className="sb-ok">
                            <Ico name="check" size={11} />
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>{t('Cost total')}</td>
              {cols.map((c) => (
                <td key={c.send.id} className={c === best ? 'win' : undefined}>
                  {c.complete ? fmtNum(Math.round(c.total)) : <span className="muted">{t('incomplet')}</span>}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {gain >= 1 ? (
        <div className="sb-mix">
          <span className="sb-mix-i">
            <Ico name="piggy" size={20} />
          </span>
          <span>
            <b>
              {split != null ? t('Comanda e împărțită: ') : t('Combinând furnizorii ai plăti ')}
              {lei(mix!)}
            </b>
            <small>
              {t(
                'cu {0} mai puțin decât cea mai bună ofertă întreagă{1}',
                lei(gain),
                split != null ? '' : t(' · o împarți din cerere, câte o comandă pe furnizor'),
              )}
            </small>
          </span>
          <button className="btn small" onClick={onOpen}>
            {split != null ? t('Deschide cererea') : t('Împarte din cerere')} <Ico name="arrow" size={13} />
          </button>
        </div>
      ) : (
        <div className="sb-cmp-f">
          <button className="link" onClick={onOpen}>
            {t('Deschide tabelul comparativ întreg →')}
          </button>
        </div>
      )}
    </>
  );
}

/** Culorile categoriilor: ordinea fixă, verificată pentru daltonism; restul e gri. */
const COLORS = ['#2a78d6', '#1baf7a', '#eb6834', '#eda100', '#e87ba4'];
const REST = '#C9CDD6';

/** Inelul: unde s-au dus banii șantierului, pe categorii sau pe furnizori. */
export function Donut({ byCategory, bySupplier }: { byCategory: LedgerShare[]; bySupplier: LedgerShare[] }) {
  const [tab, setTab] = useState<'cat' | 'sup'>('cat');
  const all = tab === 'cat' ? byCategory : bySupplier;
  const top = all.slice(0, 5);
  const rest = all.slice(5).reduce((s, r) => s + r.total, 0);
  const rows = rest > 0 ? [...top, { label: t('încă {0}', all.length - 5), total: rest }] : top;
  const total = rows.reduce((s, r) => s + r.total, 0);
  const R = 52;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="sb-donut">
      <div className="seg-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'cat'} onClick={() => setTab('cat')}>
          {t('Categorii')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'sup'} onClick={() => setTab('sup')}>
          {t('Furnizori')}
        </button>
      </div>
      {total > 0 ? (
        <div className="sb-donut-b" key={tab}>
          <svg viewBox="0 0 140 140" className="sb-ring" role="img" aria-label={t('Total {0}', lei(total))}>
            <circle cx="70" cy="70" r={R} fill="none" stroke="var(--line)" strokeWidth="18" />
            {rows.map((r, i) => {
              const len = (r.total / total) * C;
              const gap = rows.length > 1 ? Math.min(2, len / 2) : 0;
              const el = (
                <circle
                  key={r.label}
                  cx="70"
                  cy="70"
                  r={R}
                  fill="none"
                  stroke={i < top.length ? COLORS[i] : REST}
                  strokeWidth="18"
                  strokeDasharray={`${Math.max(len - gap, 0.1)} ${C}`}
                  strokeDashoffset={-acc}
                  transform="rotate(-90 70 70)"
                  className="sb-arc"
                  style={{ ['--i' as string]: i }}
                >
                  <title>{`${r.label}: ${lei(r.total)}`}</title>
                </circle>
              );
              acc += len;
              return el;
            })}
            <text x="70" y="68" textAnchor="middle" className="sb-ring-v">
              {fmtNum(Math.round(total))}
            </text>
            <text x="70" y="85" textAnchor="middle" className="sb-ring-l">
              {t('lei')}
            </text>
          </svg>
          <ul className="sb-legend">
            {rows.map((r, i) => (
              <li key={r.label}>
                <i style={{ background: i < top.length ? COLORS[i] : REST }} />
                <span>{r.label}</span>
                <b>{lei(r.total)}</b>
                <small>{Math.round((r.total / total) * 100)}%</small>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="empty" style={{ padding: '16px 0' }}>
          {t('Nicio comandă încă pe șantier.')}
        </div>
      )}
    </div>
  );
}

/** Furnizorii cererii în lucru: cine a răspuns, cu cât, față de cea mai bună ofertă. */
export function SupplierStatus({ rfq }: { rfq: Rfq }) {
  const a = analyse(rfq);
  const byId = new Map(a.cols.map((c) => [c.send.id, c]));
  const rows = [...rfq.sends].sort((x, y) => {
    const cx = byId.get(x.id);
    const cy = byId.get(y.id);
    return (cx ? (cx.complete ? cx.total : 1e15) : 2e15) - (cy ? (cy.complete ? cy.total : 1e15) : 2e15);
  });
  return (
    <ul className="sb-sups">
      {rows.map((s) => {
        const c = byId.get(s.id);
        const status = s.offer
          ? { cls: 'ok', text: t('Ofertă primită · {0}', day(s.offer.receivedAt)) }
          : s.sentAt
            ? { cls: 'wait', text: t('Cerere trimisă · {0}', day(s.sentAt)) }
            : { cls: 'off', text: t('Netrimisă încă') };
        const diff = c && c.complete && a.bestTotal != null ? c.total - a.bestTotal : null;
        return (
          <li key={s.id}>
            <Avatar name={s.supplier.name} />
            <span className="sb-sup-n">
              <b>{s.supplier.name}</b>
              <span className={`sb-st ${status.cls}`}>
                {status.cls === 'ok' ? <Ico name="check" size={11} /> : <i />}
                {t(status.text)}
              </span>
            </span>
            <span className="sb-sup-v">
              {c ? (
                <>
                  <b>{c.complete ? lei(c.total) : t('incompletă')}</b>
                  <small className={diff != null && diff < 1 ? 'best' : undefined}>
                    {diff == null ? '' : diff < 1 ? t('cea mai bună') : `+${lei(diff)}`}
                  </small>
                </>
              ) : (
                <small>{t('așteptăm')}</small>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Activitatea șantierului, ca linie de timp. */
export function Timeline({
  rows,
  now,
  onOpen,
}: {
  rows: ActivityRow[];
  now: number;
  onOpen: (r: ActivityRow) => void;
}) {
  if (!rows.length) {
    return (
      <div className="empty" style={{ padding: '16px 20px' }}>
        {t('Aici apar cererile trimise, ofertele, comenzile și livrările șantierului.')}
      </div>
    );
  }
  return (
    <ol className="sb-time">
      {rows.map((r) => (
        <li
          key={r.key}
          className={
            /cancel|reject/.test(r.kind)
              ? 'bad'
              : /offer|chosen|approved|completed|delivery/.test(r.kind)
                ? 'good'
                : ''
          }
        >
          <span className="sb-time-dot" />
          <button type="button" disabled={!r.rfqId} onClick={() => onOpen(r)}>
            <b>{t(r.text)}</b>
            <span>
              {timeAgo(r.at, now)}
              {r.actor ? ` · ${r.actor}` : ''}
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Documentele primite de la furnizori: se descarcă direct (sesiunea e în cookie). */
export function Documents({ docs }: { docs: SiteDocument[] }) {
  if (!docs.length) {
    return (
      <div className="empty" style={{ padding: '16px 20px' }}>
        {t('Ofertele în PDF și documentele primite de la furnizori apar aici.')}
      </div>
    );
  }
  return (
    <div className="sb-docs">
      {docs.map((d) => (
        <a
          key={d.id}
          className="sb-doc"
          href={`/api/rfqs/${d.rfqId}/sends/${d.sendId}/files/${d.id}`}
          title={`${d.filename} · ${d.rfqTitle}`}
        >
          <span className={`sb-doc-i${/pdf/.test(d.mime) ? ' pdf' : ''}`}>
            <Ico name="doc" size={18} />
          </span>
          <span className="sb-doc-n">
            <b>{d.filename}</b>
            <span>
              {d.supplier} · {fmtSize(d.size)}
            </span>
          </span>
          <span className="sb-doc-d">
            <Ico name="down" size={15} />
          </span>
        </a>
      ))}
    </div>
  );
}
